import { getFormProps, getSelectProps, getTextareaProps, SubmissionResult, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { z } from 'zod';
import { useState } from 'react';
import { QuoteSession } from '#app/utils/get-a-quote/quote.server.ts';
import { QuoteForm } from './quote-form';
import { quoteSubjectLineOptions, quoteSubjectValues } from '#app/utils/misc.tsx';
import { SelectField, SelectFieldItem, TextareaField } from '../forms';
import { StatusButton } from '../ui/status-button';
import { phoneRegex, socialRegex, solicitationKeywordsRegex, urlRegex } from '#app/routes/resources+/get-a-quote.tsx';
import { Button } from '../ui/button';
import { Icon } from '../ui/icon';
import { HoneypotInputs } from 'remix-utils/honeypot/react';

export const AdditionalInfoSchema = z.object({
    subject: z.enum(quoteSubjectValues, { required_error: "Please select a service" }),

    message: z.string({ required_error: "Message is required" })
        .min(10, "Please provide at least 10 characters in your message.")
        .max(1000, "Message cannot exceed 1000 characters") // Optional: Limit message length
        // --- Custom Validations for Message Content ---
        .refine(msg => !urlRegex.test(msg), {
            message: "Please remove website links (URLs) from your message."
        })
        .refine(msg => !phoneRegex.test(msg), {
            message: "Please remove phone numbers from your message."
        })
        .refine(msg => !socialRegex.test(msg), {
                message: "Please avoid mentioning specific social media platform names."
        })
        .refine(msg => !solicitationKeywordsRegex.test(msg), {
            message: "No solictation. Please summarize your garage door needs."
        }),
});

export function AdditionalInfo({ session }: { session: QuoteSession }) {
    const [lastResult, setLastResult] = useState<SubmissionResult<string[]> | null | undefined>()
    const { formData } = session;
    const [form, fields] = useForm({
        id: 'quote-additional-info',
        constraint: getZodConstraint(AdditionalInfoSchema),
        lastResult: lastResult,
        defaultValue: {
            subject: formData.subject,
            message: formData.message,
        },
        onValidate({ formData }) {
            const submission = parseWithZod(formData, { schema: AdditionalInfoSchema });
            return submission;
        }
    })

    return (
        <div className='container'>
            <div className='py-5 lg:py-10'>
                <h2 className='text-center max-w-[720px] mx-auto'>Step 4: Additional Information</h2>
                <QuoteForm
                    action={QuoteForm.ACTIONS.PreviousStep}
                    className="absolute left-0 top-0 lg:left-12 lg:top-11"
                >
                    <Button variant='secondary' type="submit">
                        <Icon name="arrow-left" />
                    </Button>
                </QuoteForm>
            </div>
            <QuoteForm
                action={QuoteForm.ACTIONS.SubmitForm}
                formProps={{...getFormProps(form)}}
                className="max-w-80 mx-auto"
            >
                {(fetcher) => {
                    const fetchedData = fetcher.data;
                    if (fetchedData !== lastResult) {
                        setLastResult(fetchedData);
                    }

                    return (
                        <>
                            <HoneypotInputs />
                            <SelectField
                            labelProps={{ children: "What can we help you with?" }}
                            selectProps={{
                                ...getSelectProps(fields.subject, {  }),
                            }}
                            errors={fields.subject.errors}
                            >
                                {quoteSubjectLineOptions.map((option) => (
                                    <SelectFieldItem value={option.value} key={option.value}>
                                        {option.name}
                                    </SelectFieldItem>
                                ))}
                            </SelectField>
                            <TextareaField
                            labelProps={{ children: "Message" }}
                            textareaProps={{
                                ...getTextareaProps(fields.message, {}),
                            }}
                            errors={fields.message.errors}
                            />
                            <StatusButton
                                type="submit"
                                status={fetcher.state !== 'idle' ? 'pending' : (form.status ?? 'idle')}
                                disabled={fetcher.state !== 'idle'}
                                className='mx-auto'
                            >
                                Next
                            </StatusButton>
                        </>
                    );
                }}
            </QuoteForm>
        </div>
    )
}